import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogActions, IconButton, Box, TextField, Button, Slide,
    Typography, FormControlLabel, Switch, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fff' },
    '& .MuiInputBase-input': { fontSize: 13 },
    '& .MuiInputLabel-root': { fontSize: 13 },
    '& .MuiFormHelperText-root': { fontSize: 11, marginLeft: 0 },
};

const RoleFormModal = ({ open, mode = 'add', role, submitting = false, onClose, onSubmit }) => {
    const isEdit = mode === 'edit';
    const [form, setForm] = useState({ role_name: '', role_code: '', description: '', is_active: true });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!open) return;
        if (isEdit && role) {
            setForm({
                role_name: role.role_name ?? '',
                role_code: role.role_code ?? '',
                description: role.description ?? '',
                is_active: role.is_active === 1 || role.is_active === true,
            });
        } else {
            setForm({ role_name: '', role_code: '', description: '', is_active: true });
        }
        setErrors({});
    }, [open, isEdit, role]);

    const setField = (name, value) => {
        setForm((p) => ({ ...p, [name]: value }));
        if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
    };

    const validate = () => {
        const e = {};
        if (!form.role_name.trim() || form.role_name.trim().length < 2) e.role_name = 'Name is required (min 2)';
        else if (form.role_name.trim().length > 50) e.role_name = 'Max 50 characters';
        if (!isEdit) {
            const code = form.role_code.trim();
            if (!code || code.length < 2) e.role_code = 'Code is required (min 2)';
            else if (code.length > 30) e.role_code = 'Max 30 characters';
            else if (!/^[A-Za-z0-9_]+$/.test(code)) e.role_code = 'Letters, numbers and underscores only';
        }
        if (form.description && form.description.length > 255) e.description = 'Max 255 characters';
        return e;
    };

    const handleSubmit = () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        const payload = {
            role_name: form.role_name.trim(),
            description: form.description.trim() || undefined,
            is_active: form.is_active ? 1 : 0,
        };
        if (isEdit) payload.id = role.role_id;
        else payload.role_code = form.role_code.trim().toUpperCase();
        onSubmit(payload, { isEdit });
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} TransitionComponent={Transition}
            maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
            <Box sx={{ px: 3, py: 2.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eef1f6' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: '#eef4ff', color: '#487fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon icon={isEdit ? 'solar:pen-new-square-bold-duotone' : 'solar:shield-plus-bold-duotone'} style={{ fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                            {isEdit ? 'Edit Role' : 'Add System Role'}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                            {isEdit ? 'Update the role details' : 'Create a new default role (starts with no permissions)'}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} disabled={submitting} size="small"
                    sx={{ color: '#64748b', bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent sx={{ px: 3, py: 2.5, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
                    <TextField label="Role Name" size="small" fullWidth required sx={fieldSx}
                        value={form.role_name} onChange={(e) => setField('role_name', e.target.value)}
                        error={!!errors.role_name} helperText={errors.role_name} />
                    <TextField label="Role Code" size="small" fullWidth required={!isEdit} sx={fieldSx}
                        value={form.role_code}
                        onChange={(e) => setField('role_code', e.target.value.toUpperCase())}
                        disabled={isEdit}
                        error={!!errors.role_code}
                        helperText={errors.role_code || (isEdit ? 'Code is immutable' : 'Uppercased, cannot be changed later')} />
                    <TextField label="Description" size="small" fullWidth multiline minRows={2} sx={fieldSx}
                        value={form.description} onChange={(e) => setField('description', e.target.value)}
                        error={!!errors.description} helperText={errors.description} />
                    <FormControlLabel
                        control={<Switch checked={form.is_active} onChange={(e) => setField('is_active', e.target.checked)} />}
                        label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Active</Typography>} />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1.25, borderTop: '1px solid #eef1f6', bgcolor: '#fbfcfe' }}>
                <Button onClick={onClose} disabled={submitting} variant="text"
                    sx={{ color: '#475569', fontWeight: 600, textTransform: 'none', px: 2 }}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={submitting} variant="contained" disableElevation
                    startIcon={submitting ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <Icon icon={isEdit ? 'solar:diskette-bold' : 'solar:add-circle-bold'} style={{ fontSize: 17 }} />}
                    sx={{ bgcolor: '#487fff', textTransform: 'none', fontWeight: 600, px: 2.5, borderRadius: 2, '&:hover': { bgcolor: '#3f6ede' } }}>
                    {submitting ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Role')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RoleFormModal;
